import { BRAND, BRAND_LONG } from "@/lib/brand";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Search,
  User,
  Phone,
  ChevronRight,
  RefreshCw,
  Home,
  ClipboardList,
  Users,
  CalendarDays,
  UserCircle,
} from "lucide-react";

function BottomNav({ active }) {
  const nav = useNavigate();
  const items = [
    { key: "home", label: "Home", icon: Home, to: "/doctor-mobile" },
    { key: "queue", label: "Queue", icon: ClipboardList, to: "/doctor-mobile" },
    { key: "patients", label: "Patients", icon: Users, to: "/doctor-mobile/patients" },
    { key: "schedule", label: "Schedule", icon: CalendarDays, to: "/reservations" },
    { key: "profile", label: "Profile", icon: UserCircle, to: "/profile" },
  ];

  return (
    <div className="fixed bottom-0 inset-x-0 z-30 bg-slate-950 border-t border-white/10 px-2 py-2">
      <div className="grid grid-cols-5 gap-1">
        {items.map((item) => {
          const Icon = item.icon;
          const on = active === item.key;
          return (
            <button
              key={item.key}
              onClick={() => nav(item.to)}
              className={`rounded-2xl py-2 flex flex-col items-center justify-center gap-1 ${
                on ? "bg-blue-600 text-white" : "text-slate-400"
              }`}
            >
              <Icon size={18} />
              <span className="text-[10px] font-semibold">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DoctorMobilePatients() {
  const nav = useNavigate();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/patients");
      setPatients(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return patients.slice(0, 100);

    return patients
      .filter((p) =>
        [p.full_name, p.patient_number, p.contact_number, p.email, p.address]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)
      )
      .slice(0, 150);
  }, [patients, q]);

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-24">
      <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur border-b border-white/10 px-4 py-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-black tracking-tight">Patients</div>
            <div className="text-xs text-cyan-300 tracking-[0.24em] uppercase">{BRAND} Mobile</div>
          </div>

          <Button size="sm" variant="outline" onClick={load} disabled={busy} className="bg-white/5 border-white/20 text-white hover:bg-white/10">
            <RefreshCw size={14} className="mr-1" />
            Refresh
          </Button>
        </div>

        <div className="relative mt-4">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search patients..."
            className="w-full rounded-2xl bg-white/10 border border-white/15 px-11 py-3 text-sm text-white placeholder:text-slate-400 outline-none"
          />
        </div>
      </div>

      <main className="p-4">
        <div className="text-xs text-slate-400 mb-3">
          {filtered.length} patient{filtered.length === 1 ? "" : "s"} found
        </div>

        <div className="space-y-3">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => nav(`/doctor-mobile/patient/${p.id}`)}
              className="w-full text-left rounded-2xl bg-white/8 border border-white/10 p-4 active:scale-[0.99]"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center shrink-0">
                    <User size={22} />
                  </div>

                  <div className="min-w-0">
                    <div className="font-bold text-lg truncate">{p.full_name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {[p.age ? `${p.age} y/o` : "", p.gender || "", p.patient_number || ""]
                        .filter(Boolean)
                        .join(" · ") || "No demographics"}
                    </div>

                    {p.contact_number ? (
                      <div className="flex items-center gap-1 text-xs text-cyan-300 mt-1">
                        <Phone size={12} />
                        {p.contact_number}
                      </div>
                    ) : null}
                  </div>
                </div>

                <ChevronRight size={18} className="text-slate-500 shrink-0" />
              </div>
            </button>
          ))}

          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-400">
              No patients found.
            </div>
          ) : null}
        </div>
      </main>

      <BottomNav active="patients" />
    </div>
  );
}
